<template>
  <div :class="$style.orderAllSplitsSummary">
    <div :class="$style.sectionBlock">
      <div
        :class="[
          $style.item,
          {
            [$style.itemWrapped]: numberOfCols === 2 && loyaltyPoints.expiredCard !== false,
          },
        ]"
      >
        <component :is="headingTag1" :class="$style.heading">
          {{ orderCartSumPriceHeading }}
        </component>
        <div :class="$style.highlightedValue" v-text="orderCartSumPrice.str" />
      </div>
      <client-only>
        <slot :is-btn-in-newline="isBtnInNewline" />
      </client-only>
    </div>
    <div v-if="loyaltyPoints.expiredCard === false" :class="$style.sectionBlock">
      <template v-for="(pointType, key) in loyaltyPoints.pointTypes" :key="key">
        <template v-if="(key === 'points') || pointType.pointValue">
          <div :class="$style.item">
            <component :is="headingTag1" :class="$style.heading">
              {{ pointType.description }}
            </component>
            <div :class="$style.highlightedValue" v-text="pointType.str" />
          </div>
        </template>
      </template>
    </div>
    <div v-else-if="loyaltyPoints.expiredCard === true" :class="$style.sectionBlock">
      <div :class="$style.descriptionBlock">
        <component :is="headingTag1" :class="$style.descriptionHeading">
          {{ $awt('aw.basket_page.order_cart_summary.expired_card.heading') }}
        </component>
        <div :class="$style.descriptionText" v-html="$awt('aw.basket_page.order_cart_summary.expired_card.description')" />
      </div>
    </div>
    <div v-else :class="$style.sectionBlock">
      <div :class="$style.descriptionBlock">
        <component :is="headingTag1" :class="$style.descriptionHeading">
          {{ $awt('aw.basket_page.order_cart_summary.no_card.heading') }}
        </component>
        <div :class="$style.descriptionText" v-html="$awt('aw.basket_page.order_cart_summary.no_card.description')" />
        <div :class="$style.descriptionLinkWrapper">
          <a :href="$awt('aw.common.loyalty_card.url')" :class="$style.descriptionLink" v-text="$awt('aw.basket_page.order_cart_summary.no_card.registration')" />
        </div>
      </div>
    </div>
    <div v-if="collectableLoyaltyStickers.pointValue" :class="$style.sectionBlock">
      <div :class="$style.item">
        <component :is="headingTag1" :class="$style.heading">
          {{ collectableLoyaltyStickers.description }}
        </component>
        <div :class="$style.highlightedValue" v-text="collectableLoyaltyStickers.str" />
      </div>
    </div>
    <div v-if="ownedLoyaltyStickers.pointValue" :class="$style.sectionBlock">
      <component :is="headingTag1" :class="$style.heading">
        {{ ownedLoyaltyStickers.description }}
      </component>
      <ul :class="['ulReset', $style.subSummary]">
        <li
          v-for="(ownedSticker, key) in ownedLoyaltyStickers.ownedStickers"
          :key="key"
          :class="$style.subSectionBlock"
        >
          <component :is="headingTag2" :class="$style.subHeading">
            {{ ownedSticker.description }}
          </component>
          <div :class="$style.highlightedValue" v-text="ownedSticker.str" />
          <div :class="$style.subInfo" v-text="ownedSticker.subtitle" />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
  import { useNuxtApp } from 'nuxt/app';
  import { formatToLoyaltyPoints } from '~~/common/plugins/price.js';
  import { useLoyaltyCardStore } from '~~/shop/app/stores/loyaltyCard.js';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { calculateHeadingChildren } from '~~/common/utils/headingTag';
  import { useOrderSplit } from '~~/common/composables/useOrderSplit';

  const props = defineProps({
    headingTag: {
      type: String,
      required: true,
      default: 'h2',
    },
  });

  const { $awt, $price, $date } = useNuxtApp();
  const { splits } = useOrderSplit();
  const numberOfSplits = computed(() => {
    return splits.value.length || 0;
  });
  const orderCartSumPriceHeading = computed(() => $awt(numberOfSplits.value > 1
    ? 'aw.basket_page.order_cart_summary.price_sum.multiple'
    : 'aw.basket_page.order_cart_summary.price_sum.single',
  ) + ':');
  const defaultCurrency = computed(() => {
    return splits.value[0]?.summary?.sum?.currency;
  });
  const orderCartSumPrice = computed(() => {
    const currency = defaultCurrency.value;
    const priceValue = splits.value.reduce(
      (acc, curr) => (acc += curr.summary.sum.grossDiscounted),
      0,
    );
    return {
      priceValue,
      currency,
      str: $price({ gross: priceValue }, currency),
    };
  });
  const loyaltyPoints = computed(() => {
    const { expectedLoyaltyPoints } = useLoyaltyCardStore();
    const { expiredCard, totalPoints } = expectedLoyaltyPoints || {};
    const { points, amount, description } = totalPoints || {};
    return {
      expiredCard,
      pointTypes: {
        points: {
          description: description + ':',
          pointValue: points,
          str: formatToLoyaltyPoints({ number: points || 0 }),
        },
        amount: {
          description: $awt('cart.page.loyalty_discount_type_1') + ':',
          pointValue: amount,
          str: $price({ gross: amount }, defaultCurrency.value),
        },
      },
    };
  });
  const collectableLoyaltyStickers = computed(() => {
    const { expectedLoyaltyPoints } = useLoyaltyCardStore();
    const { stickers } = expectedLoyaltyPoints || {};
    const quantity = (Array.isArray(stickers) ? stickers : []).reduce(
      (acc, curr) => {
        if (curr.collectable) {
          acc += curr.expectedQuantity;
        }
        return acc;
      },
      0,
    );
    return {
      description: $awt('aw.basket_page.order_cart_summary.collectable_sticker_sum.description') + ':',
      pointValue: quantity,
      str: `${quantity}`,
    };
  });
  const ownedLoyaltyStickers = computed(() => {
    const { expectedLoyaltyPoints } = useLoyaltyCardStore();
    const { stickers } = expectedLoyaltyPoints || {};
    const ownedStickers = (Array.isArray(stickers) ? stickers : []).reduce(
      (acc, curr) => {
        acc[curr.id] = {
          description: curr.name,
          pointValue: curr.availableQuantity,
          str: `${curr.availableQuantity}`,
          subtitle: $awt('aw.basket_page.sticker_info.collectable', {
            date: $date(curr.collectionEnd, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }).replaceAll(' ', ''),
          }),
        };
        return acc;
      },
      {},
    );
    return {
      description: $awt('aw.basket_page.order_cart_summary.owned_sticker_sum.description') + ':',
      ownedStickers,
      pointValue: !!Object.keys(ownedStickers).length,
    };
  });
  const numberOfCols = computed(() => {
    return (
      2 +
      !!collectableLoyaltyStickers.value.pointValue +
      !!ownedLoyaltyStickers.value.pointValue
    );
  });
  const isBtnInNewline = computed(() => {
    const screenRange = useUserInterfaceStore().mediaQueries;
    const ncol = numberOfCols.value;
    if (screenRange['mobile-max']) {
      return false;
    } else if (screenRange['tablet-max']) {
      return ncol === 3 || ncol === 4;
    } else if (screenRange['desktop-small-max']) {
      return ncol === 4;
    } else {
      return false;
    }
  });
  const headingTag1 = computed(() => {
    return calculateHeadingChildren(props.headingTag);
  });
  const headingTag2 = computed(() => {
    return calculateHeadingChildren(headingTag1.value);
  });
</script>

<style module lang="scss" rel="stylesheet/scss">

%lineClampTablet {
  line-height: var(--lineHeight);
  @include tablet(min) {
    display: -webkit-box;
    overflow: hidden;
    min-height: calc(var(--lineNumber) * var(--lineHeight));
    text-overflow: ellipsis;
    -webkit-line-clamp: var(--lineNumber);
    -webkit-box-orient: vertical;
  }
}

.orderAllSplitsSummary {
  --orderAllSplitsSummaryGap: 20px;
  display: flex;
  flex-direction: column;
  gap: var(--orderAllSplitsSummaryGap);

  @include tablet(min) {
    --orderAllSplitsSummaryGap: 12px;
    flex-direction: row;
  }

  @include desktop-small (min) {
    --orderAllSplitsSummaryGap: 16px;
  }

  @include desktop-medium (min) {
    --orderAllSplitsSummaryGap: 40px;
  }
}

.sectionBlock {
  display: flex;
  align-items: start;
  flex-direction: column;
  gap: 8px;

  @include tablet(min) {
    flex: 1 1 0;
    gap: 12px;

    &:not(:last-child) {
      padding-right: var(--orderAllSplitsSummaryGap);
      border-right: 1px dashed $color-dash-separator;
    }
  }

  .item {
    display: flex;
    align-items: start;
    flex-direction: column;
    gap: 8px;

    @include tablet (min) {
      flex: 1 1 0;
      gap: 12px;
      @include tablet(max) {
        &Wrapped {
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
        }
      }
    }
  }

  @mixin itemDirection ($colCount) {
    &:nth-child(1):not(:nth-last-child(n + #{$colCount})) {
      &,
      ~ .sectionBlock {
        .item {
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
        }
      }
    }
  }

  @include desktop-small (min) {
    @include desktop-medium (max) {
      @include itemDirection(3);
    }
  }
  @include desktop-large (min) {
    @include itemDirection(4);
  }

  @include tablet(min) {
    @include tablet(max) {
      &:nth-child(1):nth-last-child(4) {
        &,
        ~ .sectionBlock {
          .heading {
            --lineNumber: 3;
          }
        }
      }
    }
  }
}

.highlightedValue {
  @include font(900, 16px, 24px);
  display: inline-flex;
  padding: 4px 8px;
  color: $color-text-sage;
  border-radius: 8px;
  background: $color-sage-450--12;
}

.heading {
  --lineHeight: 16px;
  --lineNumber: 2;
  @include font(500, 12px, null);
  @extend %lineClampTablet;
}

.subSummary {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
}

.subSectionBlock {
  display: flex;
  align-items: start;
  flex-direction: column;
  gap: 8px;

  .highlightedValue {
    order: 1;
  }

  @include tablet(min) {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    flex-direction: row;
    justify-content: space-between;
    gap: 4px 8px;

    .highlightedValue {
      grid-column: 2;
      grid-row: 1 / -1;
    }
  }
}

.subInfo {
  color: $color-text-secondary;
}

.subHeading {
  @include font(900, 12px, 16px);
}

.descriptionHeading {
  @include font(900, 12px, 12px);
}

.descriptionText,
.subInfo {
  @include font(500, 12px, 16px);
}

.descriptionLink {
  @include font(700, 12px, 16px);
  text-decoration: underline;
}

.descriptionLinkWrapper {
  display: flex;
}

.descriptionBlock {
  padding: 12px;
  border: 1px dashed $color-dash-separator;
  border-radius: 12px;

  @include tablet(min) {
    padding: 20px;
  }
}
</style>
